import { app } from "@/main";
import { backend } from "@/api/backend";
import router from "@/router";
import {
  getLocalToken,
  removeLocalToken,
  removeMicrosoftCookieToken,
  removeMicrosoftRefreshCookieToken,
  saveLocalToken,
  setMicrosoftCookieToken,
} from "@/utils";
import type { AxiosError } from "axios";
import type { ActionContext, ActionTree, Store } from "vuex";
import type { State } from "@/store/state";
import { getMsalInstance } from "@/msal";

import { AppNotification, MainState } from "./state";
import { IUserProfileUpdate } from "@/interfaces";
import { readMsalConfig } from "@/store/main/getters";
import { loginRequestScopes } from "@/oauthConfig";

type MainContext = ActionContext<MainState, State>;

export const actions: ActionTree<MainState, State> = {
  async actionUpdateUserProfile(
    context,
    payload: IUserProfileUpdate,
  ): Promise<void> {
    try {
      const loadingNotification = { content: "saving", showProgress: true };
      context.commit("addNotification", loadingNotification);
      const response = (
        await Promise.all([
          backend.updateMe(context.state.token, payload),
          await new Promise<void>((resolve) =>
            setTimeout(() => resolve(), 500),
          ),
        ])
      )[0];
      context.commit("setUserProfile", response.data);
      context.commit("removeNotification", loadingNotification);
      context.commit("addNotification", {
        content: "Profile successfully updated",
        color: "success",
      });
    } catch (error) {
      await context.dispatch("actionCheckApiError", error);
    }
  },
  async actionCheckLoggedIn(context) {
    // Checked on each route navigation to ensure that a user is logged in
    if (!context.state.isLoggedIn) {
      let token = context.state.token;

      if (!token) {
        const localToken = getLocalToken();
        if (localToken) {
          context.commit("setToken", localToken);
          token = localToken;
        }
      }

      if (token) {
        try {
          const response = await backend.getMe(token);
          context.commit("setLoggedIn", true);
          context.commit("setUserProfile", response.data);

          app.config.globalProperties.$cookies.set(
            "access_token_cookie",
            token,
          );
        } catch {
          await context.dispatch("actionRemoveLogIn");
        }
      } else {
        await context.dispatch("actionRemoveLogIn");
      }
    }
  },

  async actionRemoveLogIn(context: MainContext): Promise<void> {
    // console.log("actionRemoveLogIn triggered");
    removeLocalToken();
    removeMicrosoftCookieToken();
    removeMicrosoftRefreshCookieToken();
    context.commit("setToken", "");
    context.commit("setLoggedIn", false);
    context.commit("setSSOLoggedIn", false);

    // const clientApp = await getMsalInstance(readMsalConfig(context.state));
    // await clientApp.logoutRedirect({ account: clientApp.getAllAccounts()[0], postLogoutRedirectUri: window.location.origin + "/login", });
  },

  async actionGetOauthConfig(context: MainContext): Promise<void> {
    try {
      const response = await backend.getOauthConfig(context.state.token);
      if (response.data && response.data.clientId && response.data.authority) {
        context.commit("setOauthConfig", response.data);
      }
    } catch (error) {
      console.error("Failed to fetch OAuth config", error);
    }
  },

  async actionLogIn(
    context: MainContext,
    payload: { username: string; password: string },
  ): Promise<void> {
    try {
      const response = await backend.logInGetToken(
        payload.username,
        payload.password,
      );
      const token = response.data.access_token;

      if (token) {
        saveLocalToken(token);
        context.commit("setToken", token);
        context.commit("setLoggedIn", true);
        context.commit("setLogInError", false);
        await context.dispatch("actionGetUserProfile");
        await context.dispatch("actionRouteLoggedIn");
        context.commit("addNotification", {
          content: "Logged in",
          color: "success",
        });
      } else {
        console.error("Login response missing access_token");
        context.commit("setLogInError", true);
        await context.dispatch("actionRemoveLogIn");
      }
    } catch (err: any) {
      console.error("Login error", err);
      if (
        err.response &&
        err.response.status !== 400 &&
        err.response.status !== 401
      ) {
        context.commit("addNotification", {
          content: "Server unavailable",
          color: "error",
        });
      }
      context.commit("setLogInError", true);
      await context.dispatch("actionRemoveLogIn");
    }
  },

  async actionGetUserProfile(context: MainContext): Promise<void> {
    try {
      const response = await backend.getMe(context.state.token);
      if (response.data) {
        context.commit("setUserProfile", response.data);
      }
    } catch (error) {
      await context.dispatch("actionCheckApiError", error);
    }
  },

  async actionLogInSSO(context: MainContext, account: any): Promise<void> {
    const token = account?.idToken;
    try {
      saveLocalToken(token);
      setMicrosoftCookieToken(token);
      context.commit("setToken", token);

      if (!context.state.isLoggedIn) {
        context.commit("addNotification", {
          content: "Logged in",
          color: "success",
        });
      }
      context.commit("setSSOLoggedIn", true);
      context.commit("setLoggedIn", true);
      context.commit("setLogInError", false);

      await context.dispatch("actionGetUserProfile");
      await context.dispatch("actionRouteLoggedIn");
    } catch (err) {
      console.error("SSO login error", err);
      context.commit("setLogInError", true);
      await context.dispatch("actionRemoveLogIn");
    }
  },

  async actionLogInSSOFailed(context: MainContext, error: any): Promise<void> {
    context.commit("addNotification", {
      content: "Backend Server Error - Failed to login via SSO",
      color: "error",
    });
    console.error("SSO login failed", error);
    context.commit("setLogInError", true);
    await context.dispatch("actionRemoveLogIn");
  },

  async actionLogInWithKey(
    context: MainContext,
    credential: any,
  ): Promise<void> {
    try {
      const response = await backend.verifyWebAuthnAuth(credential);
      const token = response.data.access_token;

      if (token) {
        saveLocalToken(token);
        context.commit("setToken", token);
        context.commit("setLoggedIn", true);
        context.commit("setLogInError", false);
        await context.dispatch("actionGetUserProfile");
        await context.dispatch("actionRouteLoggedIn");
        context.commit("addNotification", {
          content: "Logged in with key",
          color: "success",
        });
      } else {
        console.error("Login with key response missing access_token");
        context.commit("setLogInError", true);
        await context.dispatch("actionRemoveLogIn");
      }
    } catch (err: any) {
      console.error("Login with key error", err);
      if (
        err.response &&
        err.response.status !== 400 &&
        err.response.status !== 401
      ) {
        context.commit("addNotification", {
          content: "Server unavailable",
          color: "error",
        });
      }
      context.commit("setLogInError", true);
      await context.dispatch("actionRemoveLogIn");
    }
  },
  async actionRouteLogOut(): Promise<void> {
    if (router.currentRoute.value.path !== "/login") {
      await router.push("/login");
    }
  },
  async actionLogOut(context: MainContext): Promise<void> {
    await backend.logout(context.state.token);
    await context.dispatch("actionRemoveLogIn");
    await context.dispatch("actionRouteLogOut");
  },
  async actionUserLogOut(context: MainContext): Promise<void> {
    await context.dispatch("actionLogOut");
    context.commit("addNotification", {
      content: "Logged out",
      color: "success",
    });
  },
  async actionCheckApiError(
    context: MainContext,
    payload: AxiosError,
  ): Promise<void> {
    if (payload.response?.status === 401) {
      await context.dispatch("actionLogOut");
    }
  },
  async actionRouteLoggedIn(): Promise<void> {
    if (router.currentRoute.value.path === "/login") {
      await router.push("/main/state");
    }
  },
  async actionRefreshSSOToken(context: MainContext): Promise<void> {
    const clientApp = await getMsalInstance(readMsalConfig(context.state));
    const silentRequest = {
      account: clientApp.getAllAccounts()[0],
      scopes: loginRequestScopes,
      forceRefresh: true,
    };
    await clientApp
      .acquireTokenSilent(silentRequest)
      .then((tokenResponse) =>
        context.dispatch("actionLogInSSO", tokenResponse),
      )
      .catch((error) => {
        // TODO: handle network errors gracefully
        console.log(error);
        context.dispatch("actionLogInSSOFailed", error);
      });
  },
  async removeNotification(
    context: MainContext,
    payload: { notification: AppNotification; timeout: number },
  ): Promise<boolean> {
    return new Promise((resolve) => {
      setTimeout(() => {
        context.commit("removeNotification", payload.notification);
        resolve(true);
      }, payload.timeout);
    });
  },
  async passwordRecovery(
    context: MainContext,
    payload: { username: string },
  ): Promise<void> {
    const loadingNotification = {
      content: "Sending password recovery email",
      showProgress: true,
    };
    try {
      context.commit("addNotification", loadingNotification);
      await Promise.all([
        backend.passwordRecovery(payload.username),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]);
      context.commit("removeNotification", loadingNotification);
      context.commit("addNotification", {
        content: "Password recovery email sent",
        color: "success",
      });
      await context.dispatch("actionLogOut");
    } catch {
      context.commit("removeNotification", loadingNotification);
      context.commit("addNotification", {
        color: "error",
        content: "Incorrect username",
      });
    }
  },
  async resetPassword(
    context: MainContext,
    payload: { password: string; token: string },
  ): Promise<void> {
    const loadingNotification = {
      content: "Resetting password",
      showProgress: true,
    };
    try {
      context.commit("addNotification", loadingNotification);
      await Promise.all([
        backend.resetPassword(payload.password, payload.token),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]);
      context.commit("removeNotification", loadingNotification);
      context.commit("addNotification", {
        content: "Password successfully reset",
        color: "success",
      });
      await context.dispatch("actionLogOut");
    } catch {
      context.commit("removeNotification", loadingNotification);
      context.commit("addNotification", {
        color: "error",
        content: "Error resetting password",
      });
    }
  },
};

const NS = "main/";

export const dispatchUpdateUserProfile = (
  store: Store<State>,
  payload: IUserProfileUpdate,
) => store.dispatch(`${NS}actionUpdateUserProfile`, payload);

export const dispatchCheckLoggedIn = (store: Store<State>) =>
  store.dispatch(`${NS}actionCheckLoggedIn`);

export const dispatchGetOauthConfig = (store: Store<State>) =>
  store.dispatch(`${NS}actionGetOauthConfig`);

export const dispatchLogIn = (
  store: Store<State>,
  payload: { username: string; password: string },
) => store.dispatch(`${NS}actionLogIn`, payload);

export const dispatchLogInSSO = (store: Store<State>, account: any) =>
  store.dispatch(`${NS}actionLogInSSO`, account);

export const dispatchLogInSSOFailed = (store: Store<State>, error: any) =>
  store.dispatch(`${NS}actionLogInSSOFailed`, error);

export const dispatchLogInWithKey = (store: Store<State>, credential: any) =>
  store.dispatch(`${NS}actionLogInWithKey`, credential);

export const dispatchLogOut = (store: Store<State>) =>
  store.dispatch(`${NS}actionLogOut`);

export const dispatchUserLogOut = (store: Store<State>) =>
  store.dispatch(`${NS}actionUserLogOut`);

export const dispatchRefreshSSOToken = (store: Store<State>) =>
  store.dispatch(`${NS}actionRefreshSSOToken`);

export const dispatchRemoveNotification = (
  store: Store<State>,
  payload: { notification: AppNotification; timeout: number },
) => store.dispatch(`${NS}removeNotification`, payload);

export const dispatchGetUserProfile = (store: Store<State>) =>
  store.dispatch(`${NS}actionGetUserProfile`);

export const dispatchCheckApiError = (
  store: Store<State>,
  payload: AxiosError,
) => store.dispatch(`${NS}actionCheckApiError`, payload);

export const dispatchRouteLoggedIn = (store: Store<State>) =>
  store.dispatch(`${NS}actionRouteLoggedIn`);

export const dispatchRemoveLogIn = (store: Store<State>) =>
  store.dispatch(`${NS}actionRemoveLogIn`);

export const dispatchRouteLogOut = (store: Store<State>) =>
  store.dispatch(`${NS}actionRouteLogOut`);

export const dispatchPasswordRecovery = (
  store: Store<State>,
  payload: { username: string },
) => store.dispatch(`${NS}passwordRecovery`, payload);

export const dispatchResetPassword = (
  store: Store<State>,
  payload: { password: string; token: string },
) => store.dispatch(`${NS}resetPassword`, payload);
